import React, { useState } from "react";
import api, { pollJob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Empty } from "@/components/common";
import { toast } from "sonner";
import { Sparkles, Loader2, ArrowRight, Share2, FileText, CheckCircle2, AlertTriangle, ListTodo, GitBranch, HelpCircle } from "lucide-react";

const SECTIONS = [
  { key: "requirements", label: "Requirements", icon: FileText, color: "#29abe2" },
  { key: "decisions", label: "Decisions", icon: CheckCircle2, color: "#10b981" },
  { key: "risks", label: "Risks", icon: AlertTriangle, color: "#ef4444" },
  { key: "action_items", label: "Action Items", icon: ListTodo, color: "#c9a227" },
];

function Sources({ sources }) {
  if (!sources?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {sources.map((s, i) => (
        <span key={i} className="text-[10px] font-mono bg-secondary text-muted-foreground px-1.5 py-0.5 rounded" title={s.quote || ""}>
          {s.ts} · {s.speaker}
        </span>
      ))}
    </div>
  );
}

export default function ClarifyStep({ meeting, refresh, setActive, mid }) {
  const [busy, setBusy] = useState(false);
  const [answers, setAnswers] = useState({});
  const analysis = meeting.analysis;

  const run = async () => {
    setBusy(true);
    try { const res = await api.post(`/meetings/${mid}/analyze`); await pollJob(res.data.job_id); toast.success("Transcript analyzed"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message || "Analysis failed"); } finally { setBusy(false); }
  };

  const answerOpen = async (q) => {
    const val = answers[q.id];
    if (!val) return;
    await api.post(`/meetings/${mid}/open-answer`, { question_id: q.id, answer: val });
    toast.success("Answer saved");
    refresh();
  };

  if (!analysis) {
    return (
      <Empty title="Analyze the transcript"
        hint="The AI reads the active transcript and extracts requirements, decisions, risks, action items and open questions — each traceable to the speaker and timestamp it came from."
        action={<Button onClick={run} disabled={busy} className="gap-2" data-testid="run-analysis">
          {busy ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />} {busy ? "Analyzing transcript…" : "Run post-meeting analysis"}
        </Button>} />
    );
  }

  const open = analysis.open_questions || [];
  const answered = open.filter((q) => q.status === "Answered" || q.answer).length;
  const readiness = open.length ? Math.round((answered / open.length) * 100) : 100;

  return (
    <div className="space-y-6">
      <div className="rounded-xl border bg-card p-5">
        <div className="text-[11px] font-semibold tracking-[0.18em] text-[#29abe2] uppercase mb-1">Executive Summary</div>
        <p className="text-sm text-foreground">{analysis.executive_summary}</p>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={run} disabled={busy} className="gap-2" data-testid="rerun-analysis">{busy ? <Loader2 className="animate-spin" size={15} /> : <Sparkles size={15} />} Re-analyze</Button>
          <Button onClick={() => setActive("generate")} className="gap-2" data-testid="go-generate">Generate documents <ArrowRight size={16} /></Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {SECTIONS.map((sec) => {
          const items = analysis[sec.key] || [];
          return (
            <div key={sec.key} className="rounded-xl border bg-card p-5">
              <div className="flex items-center gap-2 mb-3" style={{ color: sec.color }}><sec.icon size={16} /><span className="font-semibold text-foreground">{sec.label}</span><span className="text-xs text-muted-foreground">({items.length})</span></div>
              <div className="space-y-3 max-h-[280px] overflow-y-auto">
                {items.map((it, i) => (
                  <div key={i} className="text-sm border-l-2 pl-3" style={{ borderColor: sec.color }}>
                    <div className="flex items-start gap-2">
                      <span className="font-mono text-[10px] text-muted-foreground mt-0.5">{it.id}</span>
                      <span className="text-foreground">{it.description}</span>
                    </div>
                    <div className="flex gap-2 mt-1 flex-wrap">
                      {it.priority && <span className="text-[10px] bg-secondary px-1.5 py-0.5 rounded">{it.priority}</span>}
                      {it.severity && <span className="text-[10px] bg-secondary px-1.5 py-0.5 rounded">{it.severity}</span>}
                      {it.type && <span className="text-[10px] bg-secondary px-1.5 py-0.5 rounded">{it.type}</span>}
                      {it.owner && <span className="text-[10px] bg-secondary px-1.5 py-0.5 rounded">👤 {it.owner}</span>}
                    </div>
                    <Sources sources={it.sources} />
                  </div>
                ))}
                {items.length === 0 && <p className="text-xs text-muted-foreground">None detected.</p>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Agenda coverage */}
      {analysis.agenda_coverage?.length > 0 && (
        <div className="rounded-xl border bg-card p-5">
          <div className="flex items-center gap-2 mb-3 text-[#0f2557] dark:text-[#29abe2]"><GitBranch size={16} /><span className="font-semibold">Agenda Coverage</span></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {analysis.agenda_coverage.map((c, i) => (
              <div key={i} className="text-sm p-3 rounded-lg bg-secondary/50">
                <div className="flex items-center justify-between">
                  <span className="text-foreground font-medium truncate">{c.agenda_item}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.status === "Covered" ? "bg-emerald-500/15 text-emerald-600" : c.status === "Partial" ? "bg-[#c9a227]/15 text-[#a6821a]" : "bg-red-500/15 text-red-500"}`}>{c.status}</span>
                </div>
                {c.note && <div className="text-xs text-muted-foreground mt-1">{c.note}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Open questions + readiness */}
      <div className="rounded-xl border bg-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-purple-500"><HelpCircle size={16} /><span className="font-semibold text-foreground">Open Questions</span></div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-xs text-muted-foreground">Document readiness</div>
              <div className="text-sm font-semibold text-foreground">{readiness}%</div>
            </div>
            <div className="w-28 h-2 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-[#29abe2] rounded-full transition-all" style={{ width: `${readiness}%` }} /></div>
          </div>
        </div>
        <div className="space-y-3">
          {open.map((q) => (
            <div key={q.id} className="border-l-2 border-purple-500 pl-3" data-testid={`open-${q.id}`}>
              <div className="text-sm font-medium text-foreground">{q.question}</div>
              <div className="text-xs text-muted-foreground mt-0.5">Impact: {q.impact_if_unanswered} {q.blocks?.length ? `· blocks ${q.blocks.join(", ")}` : ""}</div>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {(q.options || []).map((o) => (
                  <button key={o} onClick={() => setAnswers({ ...answers, [q.id]: o })}
                    className={`text-xs px-3 py-1 rounded-lg border transition ${(answers[q.id] === o || q.answer === o) ? "bg-[#0f2557] text-white border-[#0f2557] dark:bg-[#29abe2] dark:border-[#29abe2]" : "hover:border-[#29abe2]"} ${q.recommended === o ? "ring-1 ring-[#c9a227]" : ""}`}>
                    {o} {q.recommended === o && <span className="text-[9px] text-[#c9a227]">★</span>}
                  </button>
                ))}
                <Button size="sm" variant="outline" className="h-7" onClick={() => answerOpen(q)} data-testid={`answer-open-${q.id}`}>Save</Button>
                {(q.status === "Answered" || q.answer) && <span className="text-xs text-emerald-600">✓ {q.answer}</span>}
              </div>
            </div>
          ))}
          {open.length === 0 && <p className="text-sm text-muted-foreground">No open questions — ready to generate documents.</p>}
        </div>
      </div>
    </div>
  );
}
